import { useCallback, useEffect, useRef, useState } from "react";
import {
  getSpeechRecognitionCtor,
  isSpeechRecognitionSupported,
  isSpeechSynthesisSupported,
  setChatDictationActive,
  speakText,
  stopSpeaking,
} from "../../utilis/methods/voiceAssistant/chatSpeech";

type Options = {
  onFinalTranscript: (text: string) => void;
  onError?: (message: string) => void;
};

export function useAiChatSpeech({ onFinalTranscript, onError }: Options) {
  const [voiceChatMode, setVoiceChatMode] = useState(false);
  const [isDictating, setIsDictating] = useState(false);
  const [interimText, setInterimText] = useState("");
  const recRef = useRef<SpeechRecognition | null>(null);
  const voiceChatModeRef = useRef(voiceChatMode);
  const onFinalRef = useRef(onFinalTranscript);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    voiceChatModeRef.current = voiceChatMode;
  }, [voiceChatMode]);

  useEffect(() => {
    onFinalRef.current = onFinalTranscript;
  }, [onFinalTranscript]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const stopDictation = useCallback(() => {
    const rec = recRef.current;
    recRef.current = null;
    setIsDictating(false);
    setInterimText("");
    setChatDictationActive(false);
    if (rec) {
      try {
        rec.onend = null;
        rec.onerror = null;
        rec.onresult = null;
        rec.stop();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const startDictation = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      onErrorRef.current?.("Speech recognition is not supported in this browser.");
      return;
    }
    stopDictation();
    stopSpeaking();

    const rec = new Ctor();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = navigator.language || "en-US";
    rec.maxAlternatives = 1;

    let finalText = "";

    rec.onresult = (ev: SpeechRecognitionEvent) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const part = ev.results[i][0]?.transcript ?? "";
        if (ev.results[i].isFinal) {
          finalText += part;
        } else {
          interim += part;
        }
      }
      setInterimText(interim.trim());
      if (finalText.trim()) {
        setInterimText("");
      }
    };

    rec.onerror = (ev: SpeechRecognitionErrorEvent) => {
      if (ev.error === "aborted") return;
      onErrorRef.current?.(ev.message || ev.error || "Speech recognition error.");
      stopDictation();
    };

    rec.onend = () => {
      if (recRef.current !== rec) return;
      recRef.current = null;
      setIsDictating(false);
      setInterimText("");
      setChatDictationActive(false);
      const text = finalText.trim();
      if (text) {
        onFinalRef.current(text);
      }
    };

    recRef.current = rec;
    setIsDictating(true);
    setChatDictationActive(true);
    try {
      rec.start();
    } catch {
      onErrorRef.current?.("Could not start microphone.");
      stopDictation();
    }
  }, [stopDictation]);

  const toggleDictation = useCallback(() => {
    if (isDictating) {
      stopDictation();
    } else {
      startDictation();
    }
  }, [isDictating, startDictation, stopDictation]);

  const speakMessage = useCallback(async (text: string) => {
    if (!isSpeechSynthesisSupported()) {
      onErrorRef.current?.("Text-to-speech is not supported in this browser.");
      return;
    }
    stopDictation();
    stopSpeaking();
    try {
      await speakText(text);
    } catch (err) {
      onErrorRef.current?.(
        err instanceof Error ? err.message : "Could not play speech.",
      );
    }
  }, [stopDictation]);

  const speakAssistantReply = useCallback(
    async (text: string) => {
      if (!voiceChatModeRef.current) return;
      await speakMessage(text);
    },
    [speakMessage],
  );

  useEffect(() => {
    return () => {
      stopDictation();
      stopSpeaking();
    };
  }, [stopDictation]);

  return {
    voiceChatMode,
    setVoiceChatMode,
    isDictating,
    interimText,
    toggleDictation,
    stopDictation,
    startDictation,
    speakAssistantReply,
    speakMessage,
    speechInputSupported: isSpeechRecognitionSupported(),
    speechOutputSupported: isSpeechSynthesisSupported(),
  };
}
